import React, { useMemo } from 'react';
import { formatSpecificationDisplay } from '../utils/specifications';
import { getCategoryColor } from '../utils/categories';
import { ModThumb } from './vehicle-detail/ModThumb';

interface Modification {
  id: string;
  title: string;
  description: string;
  category: string;
  vendor: string;
  cost?: number;
  install_date?: string;
  notes?: string;
  part_number?: string;
  specifications?: any;
  parentComponent?: string;
  power_gain?: number;
  torque_gain?: number;
  weight_change?: number;
  image_url?: string | null;
}

interface ModificationsTimelineViewProps {
  modifications: Modification[];
  onEdit?: (modification: Modification) => void;
  onDelete?: (id: string) => void;
  onMove?: (modification: Modification) => void;
  isOwner?: boolean;
  showPricing?: boolean;
}

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatDate = (dateString: string): string => {
  const parts = dateString?.split('-') || [];
  if (parts.length === 3) {
    const [y, m, d] = parts.map(Number);
    const localDate = new Date(y, (m || 1) - 1, d || 1);
    return localDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }
  return dateString;
};

const formatMonthYear = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
  });
};

function OwnerActions({
  modification,
  onEdit,
  onDelete,
  onMove,
}: {
  modification: Modification;
  onEdit?: (modification: Modification) => void;
  onDelete?: (id: string) => void;
  onMove?: (modification: Modification) => void;
}) {
  if (!onEdit && !onDelete && !onMove) return null;

  const handleDelete = () => {
    if (onDelete && window.confirm(`Are you sure you want to delete "${modification.title}"?`)) {
      onDelete(modification.id);
    }
  };

  return (
    <div className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity ml-2 flex shrink-0 gap-1">
      {onMove && (
        <button
          type="button"
          onClick={() => onMove(modification)}
          className="p-2 text-gray-400 hover:text-orange-300 hover:bg-white/[0.04] rounded-lg min-h-[40px] min-w-[40px] cursor-pointer"
          title="Move"
          aria-label={`Move ${modification.title}`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
          </svg>
        </button>
      )}
      {onEdit && (
        <button
          type="button"
          onClick={() => onEdit(modification)}
          className="p-2 text-gray-400 hover:text-orange-300 hover:bg-white/[0.04] rounded-lg min-h-[40px] min-w-[40px] cursor-pointer"
          title="Edit modification"
          aria-label={`Edit ${modification.title}`}
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
          </svg>
        </button>
      )}
      {onDelete && (
        <button
          type="button"
          onClick={handleDelete}
          className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg min-h-[40px] min-w-[40px] cursor-pointer"
          title="Delete modification"
          aria-label={`Delete ${modification.title}`}
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path
              fillRule="evenodd"
              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      )}
    </div>
  );
}

function TimelineCard({
  modification,
  showPricing,
  isOwner,
  onEdit,
  onDelete,
  onMove,
  muted,
}: {
  modification: Modification;
  showPricing?: boolean;
  isOwner?: boolean;
  onEdit?: (modification: Modification) => void;
  onDelete?: (id: string) => void;
  onMove?: (modification: Modification) => void;
  muted?: boolean;
}) {
  const categoryColor = getCategoryColor(modification.category);
  const specs = formatSpecificationDisplay(modification.specifications);

  return (
    <article
      className={`relative group build-section ${muted ? 'opacity-80' : ''}`}
    >
      <div className="absolute -left-[1.65rem] top-5 h-px w-5 bg-white/[0.08]" aria-hidden />
      <div className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1 flex items-start gap-3">
            <ModThumb url={modification.image_url} alt="" size="md" />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-1.5">
                <span className="inline-flex items-center gap-1.5 text-meta">
                  <span
                    className="h-1.5 w-1.5 rounded-full shrink-0"
                    style={{ backgroundColor: categoryColor }}
                    aria-hidden
                  />
                  {modification.category}
                </span>
                {modification.install_date && (
                  <span className="text-meta tabular-nums">{formatDate(modification.install_date)}</span>
                )}
              </div>
              <h4 className="heading-card text-white">{modification.title}</h4>
              {modification.vendor && <p className="text-sm text-muted mt-1">{modification.vendor}</p>}
            </div>
          </div>

          <div className="flex items-start gap-2 shrink-0">
            {showPricing && modification.cost !== undefined && (
              <span className="text-sm text-gray-200 font-medium tabular-nums pt-1">
                {modification.parentComponent
                  ? 'Included'
                  : modification.cost === 0
                    ? '-'
                    : formatCurrency(modification.cost)}
              </span>
            )}
            {isOwner && (
              <OwnerActions
                modification={modification}
                onEdit={onEdit}
                onDelete={onDelete}
                onMove={onMove}
              />
            )}
          </div>
        </div>

        {modification.description && (
          <p className="text-sm text-muted mt-3 leading-relaxed">{modification.description}</p>
        )}

        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-meta">
          {modification.part_number && (
            <span>
              Part #: <span className="text-gray-300 font-mono">{modification.part_number}</span>
            </span>
          )}
          {modification.parentComponent && (
            <span>
              Part of: <span className="text-gray-300">{modification.parentComponent}</span>
            </span>
          )}
        </div>

        {specs.length > 0 && (
          <div className="mt-4 rounded-lg border border-white/[0.06] bg-[#0a0a0a]/40 px-3 py-2.5">
            <h5 className="text-meta mb-2">Specifications</h5>
            <ul className="space-y-1">
              {specs.map((line) => (
                <li key={line} className="text-sm text-gray-300 break-words">
                  {line}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </article>
  );
}

export const ModificationsTimelineView: React.FC<ModificationsTimelineViewProps> = ({
  modifications,
  onEdit,
  onDelete,
  onMove,
  isOwner,
  showPricing,
}) => {
  const timelineData = useMemo(() => {
    const withDates = modifications.filter((mod) => mod.install_date);
    const withoutDates = modifications.filter((mod) => !mod.install_date);

    const grouped = withDates.reduce(
      (acc, mod) => {
        const date = new Date(mod.install_date!);
        const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        if (!acc[monthYear]) acc[monthYear] = [];
        acc[monthYear].push(mod);
        return acc;
      },
      {} as Record<string, Modification[]>
    );

    const sortedMonths = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

    sortedMonths.forEach((month) => {
      grouped[month].sort(
        (a, b) => new Date(b.install_date!).getTime() - new Date(a.install_date!).getTime()
      );
    });

    return { grouped, sortedMonths, withoutDates };
  }, [modifications]);

  return (
    <div className="relative pl-2 sm:pl-3">
      <div className="absolute left-[0.7rem] top-2 bottom-2 w-px bg-white/[0.06]" aria-hidden />

      <div className="space-y-8">
        {timelineData.sortedMonths.map((monthYear) => {
          const monthMods = timelineData.grouped[monthYear];
          const firstModDate = monthMods[0].install_date!;
          const monthTotal = monthMods.reduce((sum, mod) => sum + (mod.cost || 0), 0);

          return (
            <section key={monthYear} className="relative">
              <div className="relative flex items-center mb-4">
                <span
                  className="absolute left-0 h-2.5 w-2.5 rounded-full bg-orange-500 ring-4 ring-[#0a0a0a]"
                  aria-hidden
                />
                <div className="ml-7 min-w-0">
                  <h3 className="heading-card text-base sm:text-lg text-white">
                    {formatMonthYear(firstModDate)}
                  </h3>
                  <p className="text-meta mt-0.5">
                    {monthMods.length} part{monthMods.length !== 1 ? 's' : ''}
                    {showPricing && monthTotal > 0 && (
                      <span className="ml-2 tabular-nums text-gray-300">
                        {formatCurrency(monthTotal)}
                      </span>
                    )}
                  </p>
                </div>
              </div>

              <div className="ml-7 space-y-3">
                {monthMods.map((modification) => (
                  <TimelineCard
                    key={modification.id}
                    modification={modification}
                    showPricing={showPricing}
                    isOwner={isOwner}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onMove={onMove}
                  />
                ))}
              </div>
            </section>
          );
        })}

        {timelineData.withoutDates.length > 0 && (
          <section className="relative">
            <div className="relative flex items-center mb-4">
              <span
                className="absolute left-0 h-2.5 w-2.5 rounded-full bg-[#1a1a1a] ring-4 ring-[#0a0a0a]"
                aria-hidden
              />
              <div className="ml-7 min-w-0">
                <h3 className="heading-card text-base sm:text-lg text-white">Date unknown</h3>
                <p className="text-meta mt-0.5">
                  {timelineData.withoutDates.length} part
                  {timelineData.withoutDates.length !== 1 ? 's' : ''} without an install date
                  {showPricing && (
                    <span className="ml-2 tabular-nums text-gray-300">
                      {formatCurrency(
                        timelineData.withoutDates.reduce((sum, mod) => sum + (mod.cost || 0), 0)
                      )}
                    </span>
                  )}
                </p>
              </div>
            </div>

            <div className="ml-7 space-y-3">
              {timelineData.withoutDates.map((modification) => (
                <TimelineCard
                  key={modification.id}
                  modification={modification}
                  showPricing={showPricing}
                  isOwner={isOwner}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onMove={onMove}
                  muted
                />
              ))}
            </div>
          </section>
        )}

        {timelineData.sortedMonths.length === 0 && timelineData.withoutDates.length === 0 && (
          <div className="ml-7 rounded-xl border border-dashed border-white/[0.06] px-4 py-10 text-center">
            <p className="text-sm text-white mb-1">No parts on this timeline</p>
            <p className="text-meta">Try adjusting search or filters, or add install dates to parts.</p>
          </div>
        )}
      </div>
    </div>
  );
};
